import {
	Button,
	Group,
	Modal,
	NumberInput,
	Stack,
	TagsInput,
	TextInput,
	Textarea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useEffect } from "react";
import type { Account } from "../../store/accounts/accountsSlice";
import { isValidEmail } from "../../utils/validation";

export interface AccountFormData {
	name: string;
	description: string;
	contactEmail: string;
	maxSeats: number;
	tags: string[];
	reportEmails: string[];
}

interface AccountModalProps {
	opened: boolean;
	account: Account | null;
	loading: boolean;
	onClose: () => void;
	// Resolves once the save succeeded, rejects when it failed
	onSubmit: (values: AccountFormData) => Promise<void>;
}

function AccountModal({
	opened,
	account,
	loading,
	onClose,
	onSubmit,
}: AccountModalProps) {
	const form = useForm<AccountFormData>({
		initialValues: {
			name: "",
			description: "",
			contactEmail: "",
			maxSeats: 10,
			tags: [],
			reportEmails: [],
		},
		validate: {
			name: (value) => (!value ? "Name is required" : null),
			contactEmail: (value) => {
				if (!value) return "Contact email is required";
				return isValidEmail(value) ? null : "Invalid email format";
			},
			maxSeats: (value) =>
				value < -1 || value === 0
					? "Max seats must be -1 (unlimited) or positive"
					: null,
			// TagsInput has no per-entry validation of its own
			reportEmails: (value) => {
				const invalid = value.filter((email) => !isValidEmail(email));
				return invalid.length > 0
					? `Invalid email format: ${invalid.join(", ")}`
					: null;
			},
		},
	});

	useEffect(() => {
		if (account) {
			form.setValues({
				name: account.name,
				description: account.description || "",
				contactEmail: account.contactEmail,
				maxSeats: account.maxSeats,
				tags: account.tags || [],
				reportEmails: account.scheduledReports?.map((r) => r.email) ?? [],
			});
		} else if (opened) {
			form.reset();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [account?._id, opened]);
	const handleSubmit = async (values: AccountFormData) => {
		try {
			await onSubmit(values);
			form.reset();
		} catch {
			// Keep what was typed. Resetting on a rejected save swapped in the
			// create defaults, so resubmitting sent an empty subscriber list and
			// unsubscribed every report recipient. The page reports the error.
		}
	};

	const handleClose = () => {
		form.reset();
		onClose();
	};

	return (
		<Modal
			opened={opened}
			onClose={handleClose}
			title={account ? "Edit Account" : "Create Account"}
			size="md">
			<form onSubmit={form.onSubmit(handleSubmit)}>
				<Stack gap="md">
					<TextInput
						label="Name"
						placeholder="Enter account name"
						required
						{...form.getInputProps("name")}
					/>

					<Textarea
						label="Description"
						placeholder="Enter account description (optional)"
						{...form.getInputProps("description")}
					/>

					<TextInput
						label="Contact Email"
						placeholder="contact@company.com"
						required
						{...form.getInputProps("contactEmail")}
					/>

					<TagsInput
						label="Report Subscribers"
						description="These addresses are automatically subscribed to the monthly billing report, sent on the 1st of each month. Removing an address here unsubscribes it."
						placeholder="Type an email and press Enter"
						clearable
						{...form.getInputProps("reportEmails")}
					/>

					<NumberInput
						label="Max Seats"
						placeholder="Maximum number of seats (-1 for unlimited)"
						min={-1}
						{...form.getInputProps("maxSeats")}
					/>

					<Group
						justify="flex-end"
						mt="lg">
						<Button
							variant="light"
							onClick={handleClose}>
							Cancel
						</Button>
						<Button
							type="submit"
							loading={loading}>
							{account ? "Update" : "Create"}
						</Button>
					</Group>
				</Stack>
			</form>
		</Modal>
	);
}

export default AccountModal;
